/**
 * Route Guard Component
 *
 * Blocks protected ID-flow screens until their prerequisites exist.
 *
 * Ownership contract:
 * - The selfie prerequisite is answered solely by the face-to-ID transition
 *   owner (attempt-scoped readiness). The guard never reads the browser
 *   cache, never fetches artifacts, and never repairs workflow state.
 * - Prerequisites are evaluated synchronously during render; rejected
 *   children are never mounted.
 * - While the owner is still resolving the handoff (refresh/remount
 *   rehydration), the guard renders a bounded pending state — resolution
 *   itself is bounded by BestSelfieService and always settles.
 * - A guard rejection is routed through flow-level navigation
 *   (useScreenNavigation), keeping screen state and browser location in
 *   agreement. A missing selfie after the face step is an unsupported flow
 *   state and fails clearly; it never redirects to camera setup.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useScreenFlow } from '../../hooks/useScreenFlow';
import { useScreenNavigation } from '../../hooks/useScreenNavigation';
import { useFaceToIdTransition } from '../../hooks/useFaceToIdTransition';
import {
  AuthoritativeHandoffProjection,
  faceToIdTransition,
} from '../../core/FaceToIdTransition';
import { getSessionState } from '../../utils/SessionStateManager';
import type { ScreenType } from '../../types/screens';
import type { ScreenDataMap } from '../../types/screenData';
import { useLanguage } from '../../i18n';
import { logger } from '../../utils/logger';
import { NeutralSpinner } from './NeutralSpinner';
import { PipelineRecoveryNotice } from './PipelineRecoveryNotice';
import {
  isRateLimitedLifecycleError,
  verificationLifecycleService,
} from '../../services/VerificationLifecycleService';
import { isFaceOnlyComparison } from '../../services/faceMatchRecovery';

const routeGuardLogger = logger.clone({ labels: { component: 'RouteGuard' }});

type RouteRequirement = 'selfie' | 'documentImage' | 'sessionId';

interface RouteGuardProps {
  children: React.ReactNode;
  requiredData?: Array<RouteRequirement>;
}

// Map of screens to their required data
const SCREEN_REQUIREMENTS: Record<string, Array<RouteRequirement>> = {
  '/id-request': ['selfie'],
  '/id-capture': ['selfie'],
  '/id-review': ['selfie', 'documentImage'],
  '/id-processing': ['selfie', 'documentImage'],
};

const PATH_TO_SCREEN: Record<string, ScreenType> = {
  '/': 'main',
  '/loading': 'loading',
  '/camera-setup': 'camera-setup',
  '/success': 'success',
  '/failure': 'failure',
  '/id-request': 'id-request',
  '/id-capture': 'id-capture',
  '/id-review': 'id-review',
  '/id-processing': 'id-processing',
  '/selfie-retry': 'selfie-retry',
};

export function RouteGuard({ children, requiredData }: RouteGuardProps) {
  const location = useLocation();
  const { state } = useScreenFlow();
  const { navigateToScreen } = useScreenNavigation();
  const transition = useFaceToIdTransition();
  const { t } = useLanguage();
  const hasRedirectedRef = useRef(false);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);

  const requirements =
    requiredData || SCREEN_REQUIREMENTS[location.pathname] || [];
  const targetScreen = PATH_TO_SCREEN[location.pathname];

  const missingRequirements: RouteRequirement[] = [];
  for (const requirement of requirements) {
    let hasRequirement = false;

    switch (requirement) {
      case 'selfie': {
        // Attempt-scoped readiness from the transition owner is the only
        // authority; cached image content cannot establish readiness.
        hasRequirement =
          transition.status === 'ready' && !!transition.selfie;
        break;
      }

      case 'documentImage': {
        if (
          targetScreen === 'id-processing' &&
          isFaceOnlyComparison(verificationLifecycleService.getCurrent())
        ) {
          // A face-only comparison uses the ID Verify Engine retained.
          hasRequirement = true;
          break;
        }
        const sessionState = getSessionState();
        const targetScreenData = targetScreen
          ? state.screenData[targetScreen]
          : undefined;

        if (sessionState?.document?.capturedImage) {
          hasRequirement = true;
        } else if (targetScreenData) {
          const data = targetScreenData as {
            documentImage?: string;
            capturedDocument?: { image?: string };
          };
          hasRequirement =
            !!data.documentImage || !!data.capturedDocument?.image;
        }
        if (!hasRequirement) {
          const idCaptureData = state.screenData['id-capture'] as
            | ScreenDataMap['id-capture']
            | undefined;
          hasRequirement = !!idCaptureData?.documentImage;
        }
        break;
      }

      case 'sessionId': {
        const session = getSessionState();
        hasRequirement = !!session?.sessionId;
        break;
      }
    }

    if (!hasRequirement) {
      missingRequirements.push(requirement);
    }
  }

  const selfieMissing = missingRequirements.includes('selfie');

  // The owner is still resolving the handoff (e.g., refresh rehydration).
  // Bounded: BestSelfieService settles every sequence, after which the
  // status becomes 'ready' (children mount) or 'failed' (handled below).
  const selfiePending = selfieMissing && transition.status === 'resolving';

  // Classified recoverable failure on a latched terminal attempt (missing /
  // temporarily_unavailable): the session is still pending, so the
  // contract requires the explicit-recovery notice, never a terminal
  // failure. Retrieval and retry remain owned by the transition owner; the
  // guard only surfaces its state and forwards explicit user intent.
  const selfieRecoverable =
    selfieMissing &&
    transition.status === 'failed' &&
    transition.terminal &&
    transition.requiresIdHandoff &&
    transition.failure !== null &&
    transition.failure.retryable &&
    !transition.failure.terminal;
  const selfieUnrecoverable =
    selfieMissing &&
    transition.status === 'failed' &&
    transition.terminal &&
    transition.requiresIdHandoff &&
    transition.failure !== null &&
    !transition.failure.retryable;
  const selfieProjected =
    selfieMissing &&
    transition.status === 'projected' &&
    transition.projection !== null;
  const missingKey = missingRequirements.join(',');

  useEffect(() => {
    if (!selfieProjected || !transition.projection) {
      return;
    }
    hasRedirectedRef.current = true;
    const screen = verificationLifecycleService.screenFor(
      transition.projection,
    );
    if (screen === 'camera-setup') {
      faceToIdTransition.beginAttempt();
    } else if (screen !== 'failure') {
      faceToIdTransition.reset();
    }
    navigateToScreen(screen);
  }, [navigateToScreen, selfieProjected, transition.projection]);

  useEffect(() => {
    if (selfieUnrecoverable) {
      // The artifact owner already reported an internal failure, while a 410
      // expiry requires no operation mutation. The guard only projects both
      // outcomes through the authoritative failure screen.
      navigateToScreen('failure');
    }
  }, [navigateToScreen, selfieUnrecoverable]);

  const handleRecoveryRetry = useCallback(async () => {
    const owner = faceToIdTransition.getState();
    if (owner.status === 'resolving' || recoverySubmitting || !owner.failure) {
      return;
    }

    setRecoverySubmitting(true);
    try {
      const lifecycle = await verificationLifecycleService.recoverOperationFailure({
        failureCode: owner.failure.code,
        failureClass: owner.failure.artifactStatus,
      });
      const screen = verificationLifecycleService.screenFor(lifecycle);
      if (screen === 'camera-setup') {
        // Portal created the replacement capture operation; local media state
        // may now start the user-requested recapture.
        faceToIdTransition.beginAttempt();
        navigateToScreen(screen);
        return;
      }
      if (
        lifecycle.recovery.action === 'CONTINUE' &&
        lifecycle.attempt?.stage === 'SELFIE_HANDOFF'
      ) {
        await faceToIdTransition.resolveHandoff(owner.attemptId);
        return;
      }
      navigateToScreen(screen);
    } catch (error) {
      if (error instanceof AuthoritativeHandoffProjection) {
        const screen = verificationLifecycleService.screenFor(error.lifecycle);
        if (screen === 'camera-setup') {
          faceToIdTransition.beginAttempt();
        } else if (screen !== 'failure') {
          faceToIdTransition.reset();
        }
        navigateToScreen(screen);
        return;
      }
      routeGuardLogger.warn('Portal-selected artifact recovery could not complete', {
        labels: {
          errorClass: error instanceof Error ? error.name : typeof error,
        },
      });
      if (isRateLimitedLifecycleError(error)) {
        navigateToScreen('failure');
      }
    } finally {
      setRecoverySubmitting(false);
    }
  }, [navigateToScreen, recoverySubmitting]);

  useEffect(() => {
    if (
      !missingKey ||
      selfiePending ||
      selfieRecoverable ||
      selfieUnrecoverable ||
      selfieProjected ||
      hasRedirectedRef.current
    ) {
      return;
    }

    const missing = missingKey.split(',') as RouteRequirement[];
    hasRedirectedRef.current = true;
    routeGuardLogger.warn('Route access denied, missing requirements:', {
      labels: {
        path: `${location.pathname}`,
        missing: `${JSON.stringify(missing)}`,
        transitionStatus: transition.status,
      },
    });

    if (missing.includes('selfie')) {
      // Confirm the route with Portal before initializing any local artifact
      // readiness. A stale browser route is replaced; an authoritative ID
      // route may rehydrate its bounded handoff owner.
      void verificationLifecycleService.reconcile().then((lifecycle) => {
        const authoritativeScreen = verificationLifecycleService.screenFor(lifecycle);
        if (
          authoritativeScreen === 'id-request' ||
          authoritativeScreen === 'id-capture' ||
          authoritativeScreen === 'id-review' ||
          authoritativeScreen === 'id-processing'
        ) {
          const sessionId = getSessionState()?.sessionId;
          if (sessionId) {
            faceToIdTransition.beginRestoredIdFlow(sessionId);
          } else {
            navigateToScreen('failure');
          }
          return;
        }
        navigateToScreen(authoritativeScreen);
      }).catch((error: unknown) => {
        routeGuardLogger.warn('Unable to project guarded route from Portal', {
          labels: {
            errorClass: error instanceof Error ? error.name : typeof error,
          },
        });
        navigateToScreen('failure');
      });
      return;
    }

    if (missing.includes('documentImage')) {
      navigateToScreen('id-capture');
      return;
    }

    navigateToScreen('failure', {
      errorMessage: t('failure.genericError'),
    });
  }, [
    location.pathname,
    missingKey,
    navigateToScreen,
    selfiePending,
    selfieRecoverable,
    selfieUnrecoverable,
    selfieProjected,
    t,
    transition.status,
  ]);

  if (missingRequirements.length > 0) {
    // Never mount rejected children.
    if (selfieRecoverable) {
      return (
        <PipelineRecoveryNotice retrying={recoverySubmitting} onRetry={() => void handleRecoveryRetry()} />
      );
    }
    return selfiePending ? <NeutralSpinner /> : null;
  }

  return <>{children}</>;
}
